import { renderPageShell } from '../lib/page-shell';
import {
  applyPreferences,
  authorizedFetch,
  DEFAULT_PREFERENCES,
  fetchMe,
  login,
  logout,
  register,
  resendVerification,
  savePreferences,
  type Preferences,
} from '../lib/session';
import { fetchCatalog, fetchInventory, resolvePlayerTag, playerTagHtml, type CatalogItem } from '../lib/catalog';
import { escapeHtml } from '../lib/format';
import type { RouteHandler } from '../lib/router';

interface MatchHistoryEntry {
  opponent_label: string;
  is_ranked: number;
  player_score: number;
  opponent_score: number;
  result: 'win' | 'loss' | 'tie';
  rating_delta: number | null;
  played_at: number;
}

async function fetchPlayerTagHtml(username: string): Promise<string> {
  const [catalog, inventory] = await Promise.all([fetchCatalog(), fetchInventory()]);
  const byId = new Map<string, CatalogItem>(catalog.items.map((item) => [item.id, item]));
  const tag = resolvePlayerTag(byId, inventory.equipped);
  return playerTagHtml(username, tag, escapeHtml);
}

export const renderAccount: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'account', {
    title: 'ACCOUNT',
    subtitle: 'Sign in to keep ranked stats, match history and cosmetics tied to you.',
    body: `<div id="account-mount"></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#account-mount')!;

  async function renderLoggedIn() {
    const me = await fetchMe();
    if (!me) {
      renderForms();
      return;
    }
    // Preferences travel with /api/me; apply them so the page reflects them now.
    applyPreferences(me.preferences);
    // The player's own equipped call sign/banner — previously never
    // resolved anywhere on this page at all (owner bug report, 2026-08-18).
    // Best-effort: a catalog/inventory hiccup falls back to a bare
    // username rather than blocking the rest of the account page.
    const tagHtml = await fetchPlayerTagHtml(me.user.username).catch(() => `<h2>${escapeHtml(me.user.username)}</h2>`);
    mount.innerHTML = `
      <div class="handoff">
        ${tagHtml}
        ${
          !me.user.emailVerified
            ? `<p class="form-error" data-tone="error">
                Your email isn't verified yet. <strong>Ranked queue is locked</strong> until it is.
                Casual and practice play are unaffected.
                <button class="button button--secondary" type="button" data-action="resend-verification">Resend verification email</button>
              </p>
              <p class="settings-hint" data-role="resend-status" aria-live="polite"></p>`
            : ''
        }
        <ul class="match-facts" aria-label="Player summary">
          <li><strong>${me.rating.rating}</strong><span>RATING</span></li>
          <li><strong>${me.user.level}</strong><span>LEVEL</span></li>
          <li><strong>${me.rating.wins}-${me.rating.losses}-${me.rating.ties}</strong><span>W-L-T</span></li>
        </ul>
        <div class="hero-actions">
          <button class="button button--primary" type="button" data-action="stats">View full stats</button>
          <button class="button button--secondary" type="button" data-action="achievements">Achievements</button>
          <button class="button button--secondary" type="button" data-action="challenges">Challenges</button>
          <button class="button button--secondary" type="button" data-action="friends">Friends</button>
          <button class="button button--secondary" type="button" data-action="inventory">Inventory</button>
          <button class="button button--secondary" type="button" data-action="logout">Log out</button>
        </div>
        <h2 class="history-heading">Accessibility &amp; display</h2>
        <p class="settings-hint">Saved to your account, so they follow you to any device.</p>
        <ul class="settings-list" aria-label="Preferences">
          <li>
            <label>
              <input type="checkbox" data-pref="reducedMotion" ${me.preferences.reducedMotion ? 'checked' : ''} />
              <span>Reduce motion</span>
            </label>
          </li>
          <li>
            <label>
              <input type="checkbox" data-pref="highContrast" ${me.preferences.highContrast ? 'checked' : ''} />
              <span>Higher contrast</span>
            </label>
          </li>
        </ul>
        <h2 class="history-heading">Notifications</h2>
        <p class="settings-hint">Choose what you’d want to be told about. Saved to your account and applied wherever you sign in.</p>
        <ul class="settings-list" aria-label="Notification preferences">
          <li>
            <label>
              <input type="checkbox" data-pref="notifyFriendRequests" ${me.preferences.notifyFriendRequests ? 'checked' : ''} />
              <span>Friend requests</span>
            </label>
          </li>
          <li>
            <label>
              <input type="checkbox" data-pref="notifyDirectChallenges" ${me.preferences.notifyDirectChallenges ? 'checked' : ''} />
              <span>Direct challenges from friends</span>
            </label>
          </li>
          <li>
            <label>
              <input type="checkbox" data-pref="notifyMatchResults" ${me.preferences.notifyMatchResults ? 'checked' : ''} />
              <span>Ranked match results</span>
            </label>
          </li>
        </ul>
        <p class="form-error" data-role="pref-status" aria-live="polite"></p>
        <h2 class="history-heading">Recent matches</h2>
        <ul class="attempt-list" id="history-list"><li class="attempt-list__empty">Loading…</li></ul>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="stats"]')?.addEventListener('click', () => {
      navigate('stats');
    });
    mount.querySelector<HTMLButtonElement>('[data-action="achievements"]')?.addEventListener('click', () => {
      navigate('achievements');
    });
    mount.querySelector<HTMLButtonElement>('[data-action="challenges"]')?.addEventListener('click', () => {
      navigate('challenges');
    });
    mount.querySelector<HTMLButtonElement>('[data-action="friends"]')?.addEventListener('click', () => {
      navigate('friends');
    });
    mount.querySelector<HTMLButtonElement>('[data-action="inventory"]')?.addEventListener('click', () => {
      navigate('inventory');
    });
    mount.querySelector<HTMLButtonElement>('[data-action="logout"]')?.addEventListener('click', async () => {
      await logout();
      applyPreferences(DEFAULT_PREFERENCES);
      renderLoggedIn();
    });
    mount.querySelector<HTMLButtonElement>('[data-action="resend-verification"]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const status = mount.querySelector<HTMLParagraphElement>('[data-role="resend-status"]');
      button.disabled = true;
      try {
        await resendVerification();
        if (status) status.textContent = 'Sent. Check your email.';
      } catch (error) {
        if (status) status.textContent = error instanceof Error ? error.message : 'Could not resend.';
      } finally {
        button.disabled = false;
      }
    });
    wirePreferences(me.preferences);
    renderHistory();
  }

  function wirePreferences(current: Preferences) {
    const status = mount.querySelector<HTMLParagraphElement>('[data-role="pref-status"]');
    const state: Preferences = { ...current };
    const checkboxes = mount.querySelectorAll<HTMLInputElement>('[data-pref]');
    checkboxes.forEach((box) => {
      box.addEventListener('change', async () => {
        const key = box.dataset.pref as keyof Preferences;
        state[key] = box.checked;
        // Reflect immediately for responsiveness, then persist.
        applyPreferences(state);
        try {
          const saved = await savePreferences(state);
          Object.assign(state, saved);
          applyPreferences(state);
          if (status) status.textContent = 'Saved.';
        } catch {
          if (status) status.textContent = 'Could not save. Check your connection.';
        }
      });
    });
  }

  async function renderHistory() {
    const historyList = mount.querySelector<HTMLUListElement>('#history-list');
    if (!historyList) return;
    try {
      const { history } = await authorizedFetch<{ history: MatchHistoryEntry[] }>('/api/history');
      if (history.length === 0) {
        historyList.innerHTML = `<li class="attempt-list__empty">No matches played yet.</li>`;
        return;
      }
      historyList.innerHTML = history
        .map((entry) => {
          const resultLabel = entry.result === 'win' ? 'WIN' : entry.result === 'loss' ? 'LOSS' : 'TIE';
          const deltaLabel = entry.rating_delta !== null ? ` (${entry.rating_delta >= 0 ? '+' : ''}${entry.rating_delta})` : '';
          return `
            <li>
              <span>${resultLabel}${entry.is_ranked ? ' · Ranked' : ''}</span>
              <span>vs ${escapeHtml(entry.opponent_label)} ${entry.player_score}-${entry.opponent_score}</span>
              <strong>${deltaLabel}</strong>
            </li>
          `;
        })
        .join('');
    } catch {
      historyList.innerHTML = `<li class="attempt-list__empty">Could not load history.</li>`;
    }
  }

  function renderForms() {
    mount.innerHTML = `
      <div class="choice-grid">
        <form class="choice-card" id="login-form" aria-labelledby="login-heading">
          <strong id="login-heading">Log in</strong>
          <input class="join-input" name="username" placeholder="Username" aria-label="Username" autocomplete="username" required />
          <input class="join-input" name="password" type="password" placeholder="Password" aria-label="Password" autocomplete="current-password" required />
          <button class="button button--primary" type="submit">Log in</button>
          <button class="button button--secondary" type="button" data-action="forgot-password">Forgot password?</button>
          <p class="form-error" data-role="error" aria-live="polite"></p>
        </form>
        <form class="choice-card" id="register-form" aria-labelledby="register-heading">
          <strong id="register-heading">Create account</strong>
          <input class="join-input" name="username" placeholder="Username (3-20 chars)" aria-label="Username, 3 to 20 characters" autocomplete="username" required />
          <input class="join-input" name="email" type="email" placeholder="Email" aria-label="Email" autocomplete="email" required />
          <input class="join-input" name="password" type="password" placeholder="Password (min 8 chars)" aria-label="Password, minimum 8 characters" autocomplete="new-password" required />
          <button class="button button--secondary" type="submit">Create account</button>
          <p class="settings-hint">We'll send a verification link. Ranked queue needs it; casual and practice don't.</p>
          <p class="form-error" data-role="error" aria-live="polite"></p>
        </form>
      </div>
    `;

    const loginForm = mount.querySelector<HTMLFormElement>('#login-form')!;
    const registerForm = mount.querySelector<HTMLFormElement>('#register-form')!;

    loginForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(loginForm);
      const errorEl = loginForm.querySelector<HTMLParagraphElement>('[data-role="error"]')!;
      try {
        await login(String(formData.get('username')), String(formData.get('password')));
        renderLoggedIn();
      } catch (error) {
        errorEl.textContent = error instanceof Error ? error.message : 'Login failed.';
      }
    });
    loginForm.querySelector<HTMLButtonElement>('[data-action="forgot-password"]')?.addEventListener('click', () => {
      navigate('forgot-password');
    });

    registerForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(registerForm);
      const errorEl = registerForm.querySelector<HTMLParagraphElement>('[data-role="error"]')!;
      try {
        await register(String(formData.get('username')), String(formData.get('password')), String(formData.get('email')));
        renderLoggedIn();
      } catch (error) {
        errorEl.textContent = error instanceof Error ? error.message : 'Registration failed.';
      }
    });
  }

  renderLoggedIn();
};
